import type { MessageTemplate } from '../types';

export const EN_MESSAGES = {
  'app.arenaLabel': 'Battle arena',
  'app.bot': 'BOT',
  'app.next': 'NEXT',
  'app.nextCard': 'Next card',
  'app.hand': 'Cards in hand',
  'app.tagline': 'DETERMINISTIC BATTLES',
  'app.brandDescription': 'One engine. Real-time strategy. Verifiable replays.',
  'app.pause': 'PAUSE',
  'app.resume': 'RESUME',
  'app.settings': 'SETTINGS',
  'app.debug': 'DEBUG',
  'app.engineVersion': 'ENGINE {engineVersion} · {count} CARDS',
  'start.title': 'ROYAL BATTLE FROM ZERO',
  'start.description': 'Build two independent 8-card decks from one catalog.',
  'profile.label': 'PLAYER NAME',
  'profile.placeholder': 'Player',
  'profile.aria': 'Player name',
  'deck.builder': 'Deck builder',
  'deck.mine': 'MY DECK',
  'deck.bot': 'BOT DECK',
  'deck.titleMine': 'MY DECK · {count}/8',
  'deck.titleBot': 'BOT DECK · {count}/8',
  'deck.average': 'Average elixir {value}',
  'deck.slots': 'Eight deck slots',
  'deck.slot': 'Slot {slot}: {card}',
  'deck.catalog': 'SHARED CATALOG',
  'deck.catalogAria': 'Card catalog',
  'deck.reset': 'RESET',
  'deck.cardAria': '{card}, {cost} elixir{selected}',
  'deck.alreadySelected': ', already in deck',
  'bot.configuration': 'AI configuration',
  'bot.variant': 'Bot variant',
  'bot.learningLoading': 'Learning: loading…',
  'bot.modelPreparing': 'Preparing model',
  'bot.resetLearning': 'RESET BOT LEARNING',
  'bot.learning': 'Learning: {count} battles · confidence {confidence}%',
  'bot.learningOff': 'Learning is disabled for this bot',
  'bot.lastDeck': ' · last deck: {deck}',
  'bot.modelCurrent': 'Current model: {style} · hash {hash}{lastDeck}',
  'bot.modelEmpty': 'No history yet — the manual deck will be used',
  'bot.classicPolicy': 'Deck and decisions use the classic deterministic policy{lastDeck}',
  'bot.classic': 'Classic',
  'bot.adaptive': 'Adaptive counter bot',
  'bot.learningReset': 'Bot learning was reset',
  'bot.learningResetFailed': 'Could not reset learning: {error}',
  'bot.modelRepaired': 'The bot learning model was incompatible and was reset',
  'bot.learningUnavailable': 'Bot learning is unavailable: {error}',
  'bot.preferenceRepaired': 'Unknown bot variant was replaced with the adaptive bot',
  'style.balanced': 'balanced',
  'style.aggression': 'aggressive',
  'style.cycle': 'cycle',
  'style.beatdown': 'beatdown',
  'style.airPressure': 'air pressure',
  'style.swarm': 'swarm',
  'style.spellControl': 'spell control',
  'style.defense': 'defensive',
  'start.playBot': 'PLAY AGAINST BOT',
  'start.loadingArena': 'LOADING ARENA…',
  'start.lastBattle': 'LAST BATTLE AND LOG',
  'start.settingsLogs': 'SETTINGS AND LOGS',
  'start.controls': 'Mouse / touch · keys 1–4 · Esc cancels · Space pauses',
  'network.title': 'ONLINE 1×1',
  'network.description': 'Create a private room or join with a code. Both peers run the same deterministic engine.',
  'network.create': 'CREATE ROOM',
  'network.join': 'JOIN ROOM',
  'network.codePlaceholder': 'ROOM CODE',
  'network.codeAria': 'Room code',
  'network.cancel': 'CANCEL',
  'network.copyCode': 'COPY CODE',
  'network.copyInvite': 'COPY INVITE LINK',
  'network.acceptInvite': 'ACCEPT CHALLENGE',
  'network.leaderboard': 'FRIENDLY LEADERBOARD',
  'network.waiting': 'Waiting for an opponent…',
  'network.connecting': 'Connecting peers…',
  'network.ready': 'Opponent connected. Starting match…',
  'network.cancelled': 'Room closed. You can create a new invitation.',
  'network.connectionTimeout': 'The opponent accepted, but the browsers could not establish a P2P connection. The room was closed; create a new invitation and try again.',
  'network.unavailable': 'Online play is unavailable: {error}',
  'network.roomCreated': 'Room {code} created. Share the code with your opponent.',
  'network.joining': 'Joining room {code}…',
  'network.codeCopied': 'Room code copied',
  'network.inviteCopied': 'Private invite link copied',
  'network.acceptingInvite': 'Accepting the challenge…',
  'network.invitedBy': '{name} challenges you to a private 1×1 battle',
  'network.hostRating': 'Host: {wins} wins in {games} network games',
  'network.headToHead': 'Previous meetings: host {hostWins} · you {guestWins} · draws {draws}',
  'network.inviteUnavailable': 'This invitation is invalid, expired or already used.',
  'network.copyFailed': 'Could not copy the room code',
  'network.disconnected': 'Connection to the opponent was lost',
  'network.reconnecting': 'Connection interrupted. Reconnecting…',
  'network.reconnected': 'Peer-to-peer connection restored',
  'network.connectionLost': 'The opponent connection could not be restored. The battle was closed; you can create a new room.',
  'network.emotes': 'Battle emotes',
  'network.waitingLockstep': 'Waiting for opponent input…',
  'network.claimPending': 'Result is waiting for peer confirmation',
  'network.claimConfirmed': 'Result confirmed by both peers',
  'network.claimServerVerified': 'Result verified by the server replay',
  'network.claimDisputed': 'Peer results differ; no win was recorded',
  'network.casualNotice': 'Friendly P2P results are peer-confirmed, not ranked or cheat-proof.',
  'network.rankLine': '{rank}. {name} — {wins} W / {losses} L / {draws} D',
  'result.victory': 'VICTORY',
  'result.defeat': 'DEFEAT',
  'result.draw': 'DRAW',
  'result.replayLabel': 'REPLAY · {label}',
  'result.kingDestroyed': 'KING DEFEATED',
  'result.overtimeVictory': 'OVERTIME FINISH',
  'result.tiebreak': 'TIEBREAK',
  'result.finished': 'BATTLE FINISHED',
  'result.summary': '{own}:{opponent} · {seconds}s · checksum {checksum}',
  'result.noChecksum': 'none',
  'result.analyzer': 'POST-MATCH ANALYZER',
  'result.diagnostics': '{records} records · {critical} critical · {errors} errors · {warnings} warnings',
  'result.finding': '[{ruleId}] tick {tick}',
  'result.noFindings': 'No violations found',
  'result.again': 'PLAY AGAIN',
  'result.replay': 'REPLAY',
  'result.export': 'EXPORT LOG',
  'result.home': 'HOME',
  'result.deleteLog': 'DELETE LOG',
  'result.noResult': 'The saved battle has no result',
  'result.confirmAgain': 'PRESS AGAIN',
  'result.logDeleted': 'LOG DELETED',
  'result.logDeleteFailed': 'DELETE FAILED',
  'result.logDeletedToast': 'This battle log was deleted',
  'result.logDeleteError': 'Could not delete the log: {error}',
  'settings.title': 'SETTINGS',
  'settings.music': 'Music',
  'settings.effects': 'Effects',
  'settings.quality': 'Quality',
  'settings.qualityAuto': 'Auto',
  'settings.qualityLow': 'Low',
  'settings.qualityHigh': 'High',
  'settings.mute': 'Mute audio',
  'settings.reducedMotion': 'Reduce motion',
  'settings.language': 'Language',
  'settings.privacy': 'ANALYTICS AND PRIVACY',
  'settings.metrics': 'Send anonymous gameplay metrics',
  'settings.metricsHelp': 'Visits, active time, battle counts, retention and aggregated card usage. No full battle journal.',
  'settings.logs': 'Upload full logs of completed and interrupted battles',
  'settings.logsHelp': 'On by default. Local diagnostics are always saved; this setting controls server upload.',
  'settings.deleteServerLogs': 'DELETE MY UPLOADED LOGS',
  'settings.clearLogs': 'ERASE SAVED LOGS',
  'settings.done': 'DONE',
  'settings.savedFailed': 'Settings were applied but the browser could not save them',
  'settings.logsEnabled': 'Full log uploads are enabled for completed and interrupted battles',
  'settings.logsDisabled': 'Server upload is disabled; local diagnostics stay active',
  'settings.deleteServerConfirm': 'PRESS AGAIN TO DELETE',
  'settings.serverLogsDeleted': 'UPLOADED LOGS DELETED',
  'settings.serverLogsMissing': 'NO UPLOADED LOGS FOUND',
  'settings.serverLogsDeletedToast': 'Uploaded full logs were deleted from the server',
  'settings.serverLogsMissingToast': 'No uploaded logs were found',
  'settings.serverDeleteFailed': 'DELETE FAILED',
  'settings.serverDeleteError': 'Could not delete server logs: {error}',
  'settings.clearConfirm': 'PRESS AGAIN TO ERASE',
  'settings.logsCleared': 'LOGS DELETED',
  'settings.logsClearCurrent': 'History cleared; the current battle will be saved when it ends',
  'settings.logsClearDone': 'Saved logs were deleted',
  'settings.logsClearFailed': 'ERASE FAILED',
  'settings.logsClearError': 'Could not delete logs: {error}',
  'hud.preparation': 'PREPARATION',
  'hud.regulation': 'REGULATION{multiplier}',
  'hud.overtime': 'OVERTIME ×{multiplier}',
  'hud.ended': 'FINISHED',
  'command.insufficient-elixir': 'Not enough elixir',
  'command.card-cooldown': 'The card is not ready yet',
  'command.invalid-position': 'Cannot deploy here',
  'command.card-not-in-hand': 'The card is no longer in hand',
  'command.invalid-sequence': 'A command is already pending',
  'command.late-command': 'The command arrived late',
  'command.future-command': 'The command is too early',
  'command.unknown-card': 'Unknown card',
  'command.battle-ended': 'The battle has ended',
  'command.invalid-command': 'Invalid command',
  'toast.playerSaveFailed': 'The name was applied but the browser could not save it',
  'toast.deckSaveFailed': 'Decks were applied but the browser could not save them',
  'toast.botSaveFailed': 'Bot mode was applied but the browser could not save it',
  'toast.startFailed': 'Could not start the battle: {error}',
  'toast.logSaveFailed': 'Log was not saved: {error}',
  'toast.deckRepaired': 'The saved deck was damaged or outdated — default cards were restored',
  'toast.appUpdated': 'The game was updated — press REPLAY again',
  'card.giant.name': 'Giant',
  'card.knight.name': 'Knight',
  'card.musketeer.name': 'Musketeer',
  'card.bomber.name': 'Bomber',
  'card.skeletons.name': 'Skeletons',
  'card.minions.name': 'Minions',
  'card.cannon.name': 'Cannon',
  'card.fireball.name': 'Fireball',
  'card.giant-skeleton.name': 'Giant Skeleton',
  'card.goblin-barrel.name': 'Goblin Barrel',
  'card.goblin-gang.name': 'Goblin Gang',
  'card.mega-knight.name': 'Mega Knight',
  'card.minion-horde.name': 'Minion Horde',
  'card.the-log.name': 'The Log',
  'card.witch.name': 'Witch',
  'card.wizard.name': 'Wizard',
  'arena.basecamp.name': 'Basecamp',
} as const satisfies Record<string, MessageTemplate>;
